import { IDirectMessageService } from '../interfaces/IDirectMessageService.js';
import { Connection, PublicKey, Transaction, SystemProgram } from '@solana/web3.js';
import { secureStorage } from '../../utils/security';

/**
 * Direct Message Service Implementation
 * Handles blockchain-based direct messaging for Znoospace
 */
export class DirectMessageService extends IDirectMessageService {
    constructor(connection, wallet, solanaService) {
        super();
        this.connection = connection;
        this.wallet = wallet;
        this.solanaService = solanaService;
        this.messageCache = new Map();
        this.conversationCache = new Map();
        this.cacheExpiry = 2 * 60 * 1000; // 2 minutes
        this.blockedUsers = new Set();
        
        // Load blocked users from localStorage
        this.loadBlockedUsers();
    }

    /**
     * Check if wallet is properly connected
     */
    isWalletConnected() {
        return this.wallet?.publicKey || 
               this.wallet?.connected || 
               (this.connection && this.wallet);
    }

    /**
     * Send a direct message to another user
     */
    async sendMessage(recipientWallet, content, messageType = 'text', mediaUrl = null) {
        try {
            if (!this.isWalletConnected()) {
                return { success: false, error: 'Wallet not connected' };
            }

            // Check if user is blocked
            const canSend = await this.canSendMessageTo(recipientWallet);
            if (!canSend.canSend) {
                return { success: false, error: canSend.reason };
            }

            // Validate content
            if (!content || content.trim().length === 0) {
                return { success: false, error: 'Message content cannot be empty' };
            }

            if (content.length > 1000) {
                return { success: false, error: 'Message too long (max 1000 characters)' };
            }

            // Create message object
            const messageId = `dm_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
            const timestamp = new Date().toISOString();
            
            const messageData = {
                id: messageId,
                type: 'ZNOOSPACE_DM',
                sender: this.wallet.publicKey.toString(),
                recipient: recipientWallet,
                content: content.trim(),
                messageType,
                mediaUrl,
                timestamp,
                read: false,
                deleted: false
            };

            // Try to send via blockchain transaction
            try {
                const transactionResult = await this.createMessageTransaction(messageData);
                if (transactionResult.success) {
                    // Cache the message locally
                    this.cacheMessage(messageData);
                    
                    return {
                        success: true,
                        transactionId: transactionResult.transactionId,
                        messageId: messageId
                    };
                }
            } catch (blockchainError) {
                console.warn('Blockchain message failed, using fallback:', blockchainError);
            }

            // Fallback: Store locally and simulate success
            messageData.transactionId = `fallback_${messageId}`;
            this.cacheMessage(messageData);
            
            return {
                success: true,
                transactionId: messageData.transactionId,
                messageId: messageId
            };

        } catch (error) {
            console.error('Error sending message:', error);
            return { success: false, error: error.message };
        }
    }

    /**
     * Get conversation between current user and another user
     */
    async getConversation(otherUserWallet, limit = 20, before = null) {
        try {
            if (!this.isWalletConnected()) {
                return { success: false, error: 'Wallet not connected' };
            }

            const conversationKey = this.getConversationKey(this.wallet.publicKey.toString(), otherUserWallet);
            
            // Check cache first
            const cached = this.getCachedConversation(conversationKey);
            if (cached) {
                const messages = this.paginateMessages(cached.messages, limit, before);
                return {
                    success: true,
                    messages: messages.items,
                    hasMore: messages.hasMore
                };
            }

            // Try to load from blockchain
            let messages = [];
            try {
                const blockchainMessages = await this.loadConversationFromBlockchain(otherUserWallet);
                messages = blockchainMessages;
            } catch (error) {
                console.warn('Failed to load from blockchain, using fallback:', error);
                messages = this.getFallbackConversation(otherUserWallet);
            }

            // Cache the conversation
            this.cacheConversation(conversationKey, messages);

            const paginatedMessages = this.paginateMessages(messages, limit, before);
            
            return {
                success: true,
                messages: paginatedMessages.items,
                hasMore: paginatedMessages.hasMore
            };

        } catch (error) {
            console.error('Error getting conversation:', error);
            return { success: false, error: error.message };
        }
    }

    /**
     * Get list of all conversations for current user
     */
    async getConversationList(limit = 50) {
        try {
            if (!this.isWalletConnected()) {
                return { success: false, error: 'Wallet not connected' };
            }

            // Get conversations from cache and blockchain
            const conversations = await this.loadAllConversations();
            
            // Sort by last message timestamp
            conversations.sort((a, b) => new Date(b.lastMessageTime) - new Date(a.lastMessageTime));
            
            // Apply limit
            const limitedConversations = conversations.slice(0, limit);

            return {
                success: true,
                conversations: limitedConversations
            };

        } catch (error) {
            console.error('Error getting conversation list:', error);
            return { success: false, error: error.message };
        }
    }

    /**
     * Mark messages as read in a conversation
     */
    async markMessagesAsRead(otherUserWallet, messageIds) {
        try {
            if (!this.isWalletConnected()) {
                return { success: false, error: 'Wallet not connected' };
            }

            const conversationKey = this.getConversationKey(this.wallet.publicKey.toString(), otherUserWallet);
            const cached = this.getCachedConversation(conversationKey);
            
            if (cached) {
                // Mark messages as read in cache
                cached.messages.forEach(message => {
                    if (messageIds.includes(message.id) && message.recipient === this.wallet.publicKey.toString()) {
                        message.read = true;
                    }
                });
                
                // Update cache
                this.cacheConversation(conversationKey, cached.messages);
            }

            return { success: true };

        } catch (error) {
            console.error('Error marking messages as read:', error);
            return { success: false, error: error.message };
        }
    }

    /**
     * Get unread message count for current user
     */
    async getUnreadCount() {
        try {
            if (!this.isWalletConnected()) {
                return { success: false, error: 'Wallet not connected' };
            }

            let unreadCount = 0;
            const userWallet = this.wallet.publicKey.toString();

            // Count unread messages from all cached conversations
            for (const [key, conversation] of this.conversationCache.entries()) {
                if (conversation.timestamp > Date.now() - this.cacheExpiry) {
                    conversation.messages.forEach(message => {
                        if (message.recipient === userWallet && !message.read && !message.deleted) {
                            unreadCount++;
                        }
                    });
                }
            }

            return { success: true, count: unreadCount };

        } catch (error) {
            console.error('Error getting unread count:', error);
            return { success: false, error: error.message };
        }
    }

    /**
     * Delete a message (soft delete - mark as deleted)
     */
    async deleteMessage(messageId) {
        try {
            if (!this.isWalletConnected()) {
                return { success: false, error: 'Wallet not connected' };
            }

            // Find and mark message as deleted in all cached conversations
            for (const [key, conversation] of this.conversationCache.entries()) {
                const message = conversation.messages.find(m => m.id === messageId);
                if (message && message.sender === this.wallet.publicKey.toString()) {
                    message.deleted = true;
                    this.cacheConversation(key, conversation.messages);
                    break;
                }
            }

            return { success: true };

        } catch (error) {
            console.error('Error deleting message:', error);
            return { success: false, error: error.message };
        }
    }

    /**
     * Search messages in conversations
     */
    async searchMessages(query, conversationWith = null) {
        try {
            if (!this.isWalletConnected()) {
                return { success: false, error: 'Wallet not connected' };
            }

            if (!query || query.trim().length === 0) {
                return { success: true, messages: [] };
            }

            const searchQuery = query.toLowerCase().trim();
            const results = [];

            // Search through cached conversations
            for (const [key, conversation] of this.conversationCache.entries()) {
                if (conversation.timestamp > Date.now() - this.cacheExpiry) {
                    conversation.messages.forEach(message => {
                        if (!message.deleted && message.content.toLowerCase().includes(searchQuery)) {
                            // If conversationWith is specified, filter by that conversation
                            if (!conversationWith || 
                                message.sender === conversationWith || 
                                message.recipient === conversationWith) {
                                results.push(message);
                            }
                        }
                    });
                }
            }

            // Sort by timestamp (newest first)
            results.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

            return { success: true, messages: results };

        } catch (error) {
            console.error('Error searching messages:', error);
            return { success: false, error: error.message };
        }
    }

    /**
     * Block/unblock a user from sending DMs
     */
    async setUserBlocked(userWallet, block) {
        try {
            if (!this.isWalletConnected()) {
                return { success: false, error: 'Wallet not connected' };
            }

            if (block) {
                this.blockedUsers.add(userWallet);
            } else {
                this.blockedUsers.delete(userWallet);
            }

            // Save to localStorage
            this.saveBlockedUsers();

            return { success: true };

        } catch (error) {
            console.error('Error blocking/unblocking user:', error);
            return { success: false, error: error.message };
        }
    }

    /**
     * Get list of blocked users
     */
    async getBlockedUsers() {
        try {
            return { 
                success: true, 
                blockedUsers: Array.from(this.blockedUsers) 
            };
        } catch (error) {
            console.error('Error getting blocked users:', error);
            return { success: false, error: error.message };
        }
    }

    /**
     * Check if current user can send DM to target user
     */
    async canSendMessageTo(targetWallet) {
        try {
            if (!this.isWalletConnected()) {
                return { success: false, error: 'Wallet not connected' };
            }

            if (targetWallet === this.wallet.publicKey.toString()) {
                return { 
                    success: true, 
                    canSend: false, 
                    reason: 'Cannot send message to yourself' 
                };
            }

            if (this.blockedUsers.has(targetWallet)) {
                return { 
                    success: true, 
                    canSend: false, 
                    reason: 'User is blocked' 
                };
            }

            return { success: true, canSend: true };

        } catch (error) {
            console.error('Error checking if can send message:', error);
            return { success: false, error: error.message };
        }
    }

    // Private helper methods

    async createMessageTransaction(messageData) {
        try {
            if (!this.isWalletConnected()) {
                throw new Error('Wallet not connected');
            }

            // Use the same transaction pattern as SolanaService to avoid double confirmation
            console.log('Creating DM transaction with single wallet confirmation...');
            
            // Create message content for memo
            const messageContent = `Znoospace DM: ${JSON.stringify(messageData)}`;
            
            // Create transaction
            const transaction = new Transaction();
            
            // Add memo instruction
            const memoInstruction = {
                keys: [],
                programId: new PublicKey('MemoSq4gqABAXKb96qnH8TysNcWxMyWCqXgDLGmfcHr'),
                data: Buffer.from(messageContent, 'utf8')
            };
            
            transaction.add(memoInstruction);

            // Get recent blockhash
            const { blockhash } = await this.connection.getLatestBlockhash();
            transaction.recentBlockhash = blockhash;
            transaction.feePayer = this.wallet.publicKey;

            // Single wallet confirmation - sign and send in one step
            console.log('Requesting wallet signature for DM...');
            const signedTransaction = await this.wallet.signTransaction(transaction);
            
            console.log('Sending DM transaction to blockchain...');
            const signature = await this.connection.sendRawTransaction(signedTransaction.serialize());
            
            console.log('DM transaction sent, confirming...', signature);
            await this.connection.confirmTransaction(signature);

            console.log('✅ DM transaction confirmed:', signature);
            return { success: true, transactionId: signature };

        } catch (error) {
            console.error('❌ Error creating DM transaction:', error);
            throw error;
        }
    }

    async loadConversationFromBlockchain(otherUserWallet) {
        // This would query the blockchain for message transactions
        // For now, return empty array as fallback
        return [];
    }

    async loadAllConversations() {
        const conversations = [];
        const userWallet = this.wallet.publicKey.toString();
        const conversationMap = new Map();

        // Process cached conversations
        for (const [key, conversation] of this.conversationCache.entries()) {
            if (conversation.timestamp > Date.now() - this.cacheExpiry) {
                conversation.messages.forEach(message => {
                    const otherUser = message.sender === userWallet ? message.recipient : message.sender;
                    
                    if (!conversationMap.has(otherUser)) {
                        conversationMap.set(otherUser, {
                            userWallet: otherUser,
                            lastMessage: message,
                            lastMessageTime: message.timestamp,
                            unreadCount: 0
                        });
                    } else {
                        const existing = conversationMap.get(otherUser);
                        if (new Date(message.timestamp) > new Date(existing.lastMessageTime)) {
                            existing.lastMessage = message;
                            existing.lastMessageTime = message.timestamp;
                        }
                    }

                    // Count unread messages
                    if (message.recipient === userWallet && !message.read && !message.deleted) {
                        conversationMap.get(otherUser).unreadCount++;
                    }
                });
            }
        }

        return Array.from(conversationMap.values());
    }

    getConversationKey(wallet1, wallet2) {
        // Create a consistent key for the conversation regardless of order
        return [wallet1, wallet2].sort().join('_');
    }

    cacheMessage(message) {
        const conversationKey = this.getConversationKey(message.sender, message.recipient);
        const cached = this.getCachedConversation(conversationKey);
        
        if (cached) {
            cached.messages.push(message);
            cached.messages.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
        } else {
            this.cacheConversation(conversationKey, [message]);
        }
    }

    cacheConversation(key, messages) {
        this.conversationCache.set(key, {
            messages: messages,
            timestamp: Date.now()
        });
    }

    getCachedConversation(key) {
        const cached = this.conversationCache.get(key);
        if (cached && cached.timestamp > Date.now() - this.cacheExpiry) {
            return cached;
        }
        return null;
    }

    paginateMessages(messages, limit, before) {
        let filteredMessages = messages.filter(m => !m.deleted);
        
        if (before) {
            const beforeIndex = filteredMessages.findIndex(m => m.id === before);
            if (beforeIndex > 0) {
                filteredMessages = filteredMessages.slice(0, beforeIndex);
            }
        }

        // Sort by timestamp (newest first for pagination)
        filteredMessages.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
        
        const items = filteredMessages.slice(0, limit);
        const hasMore = filteredMessages.length > limit;

        return { items, hasMore };
    }

    getFallbackConversation(otherUserWallet) {
        // Return sample conversation for development
        return [
            {
                id: `fallback_${Date.now()}_1`,
                type: 'ZNOOSPACE_DM',
                sender: otherUserWallet,
                recipient: this.wallet.publicKey.toString(),
                content: 'Hey! Welcome to Znoospace DMs! 🚀',
                messageType: 'text',
                mediaUrl: null,
                timestamp: new Date(Date.now() - 60000).toISOString(),
                read: false,
                deleted: false
            }
        ];
    }

    loadBlockedUsers() {
        try {
            const stored = secureStorage.getItem('blocked_users');
            if (stored) {
                const blockedArray = typeof stored === 'string' ? JSON.parse(stored) : stored;
                this.blockedUsers = new Set(blockedArray);
            }
        } catch (error) {
            console.error('Error loading blocked users:', error);
        }
    }

    saveBlockedUsers() {
        try {
            const blockedArray = Array.from(this.blockedUsers);
            secureStorage.setItem('blocked_users', JSON.stringify(blockedArray));
        } catch (error) {
            console.error('Error saving blocked users:', error);
        }
    }
}
